import React, { useState, useEffect } from 'react';
import Button from 'antd/lib/button';
import Overlay from '../Overlay';
import { API, graphqlOperation } from 'aws-amplify';
import { useSelector } from 'react-redux';
import message from 'antd/lib/message';
import { updateUser } from '../../redux/app/actions';

const getTermsQuery = `
  query GetTermsOfUse($pk: ID!) {
    getTermsOfUse(pk: $pk, sk: "TERMS#LATEST") {
      text
      version
      enabled
      requireReacceptance
    }
  }
`;

const updateOccupantQuery = `
  mutation UpdateOccupant($pk: ID!, $sk: String!, $expectedVersion: Int!, $input: updateOccupantInput!) {
    updateOccupant(pk: $pk, sk: $sk, expectedVersion: $expectedVersion, input: $input) {
      version
      extraFields {
        key
        value
      }
    }
  }
`;

// Sized and laid out to match the CONFIRM USER INFORMATION overlay
// (UserConfigurator) — same width, header block, and centered primary button.
const styles = {
    container: {
        padding: '1em',
        overflow: 'scroll',
        maxHeight: 670,
        width: 505
    },
    content: {
        whiteSpace: 'pre-wrap',
        marginTop: '1em',
        minHeight: 380,
        maxHeight: '45vh',
        overflow: 'auto',
        backgroundColor: '#fff',
        padding: '15px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px'
    },
    buttonContainer: {
        marginTop: '1em',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
    },
    infoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    title: {
        marginTop: '0.64em',
        color: '#1890FF'
    }
};

const TermsOfUseAcceptance = ({ onVisibilityChange, hideForUserConfig = false }) => {
    const [termsText, setTermsText] = useState('');
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const user = useSelector(state => state.app.user);
    const building = useSelector(state => state.app.building);
    const tenant = useSelector(state => state.app.tenant);
    const buildingId = building?.pk;
    const buildingSettings = building?.settings;

    // Terms of Use is the SECOND first-login popup: it waits for the Confirm
    // User Information screen, and the Accept/Decline team windows wait for it.
    const suppressed = hideForUserConfig;
    const effectiveVisible = visible && !loading && !suppressed;

    // Reported to Guardian as "terms is (or may still become) on screen":
    // true while the acceptance check is unresolved, while a first-login user
    // still has to pass through Confirm User Information (the check re-runs
    // after that), and while the popup itself shows. Downstream popups (team
    // Accept/Decline, tenant config) hold until this settles to false — a bare
    // "visible" flag would let them flash during the async gaps.
    const termsEnabled = Boolean(buildingSettings?.systemConfig?.termsOfUse);
    // userD1 lands with SET_USER_DATA, d1 only with useInit's later write —
    // same fallback as UserConfigurator so "blocking" covers the whole
    // confirmation window.
    const awaitingUserConfig = Boolean(termsEnabled && (user?.d1 || user?.userD1) && !user?.haveLoggedIn);
    const blocking = (termsEnabled && loading) || awaitingUserConfig || effectiveVisible;

    useEffect(() => {
        if (onVisibilityChange) onVisibilityChange(blocking);
    }, [blocking, onVisibilityChange]);

    useEffect(() => {
        const checkTermsAcceptance = async () => {
            if (!buildingId || !user) {
                setLoading(false);
                return;
            }

            // Check if user configuration is complete (user has basic required fields)
            const hasRequiredUserData = user.firstName && user.lastName && user.d1;
            if (!hasRequiredUserData) {
                setLoading(false);
                setVisible(false);
                return;
            }

            // First-login users must finish the CONFIRM USER INFORMATION
            // screen before Terms shows. UserConfigurator keys off this same
            // flag and re-asserts it when the user saves, which re-runs this
            // check via the user dependency. `loading` deliberately stays true
            // so the blocking signal has no gap between the save and the
            // re-check — a gap would let the team popups flash in between.
            if (!user.haveLoggedIn) {
                setVisible(false);
                return;
            }

            // First check if terms of use is enabled in building settings
            const termsEnabled = buildingSettings?.systemConfig?.termsOfUse;
            if (!termsEnabled) {
                setLoading(false);
                setVisible(false);
                return;
            }

            try {
                setLoading(true);
                const response = await API.graphql(graphqlOperation(getTermsQuery, {
                    pk: buildingId
                }));

                const terms = response?.data?.getTermsOfUse;
                if (!terms || !terms.enabled) {
                    setLoading(false);
                    setVisible(false);
                    return;
                }

                // Validate terms version
                if (terms.version < 1) {
                    console.error('[Terms Check] Invalid terms version:', terms.version);
                    setLoading(false);
                    setVisible(false);
                    return;
                }

                setTermsText(terms.text || '');

                // Get the user's accepted version from extraFields for this specific building
                const acceptedVersionField = user.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
                const acceptedVersion = acceptedVersionField ? parseInt(acceptedVersionField.value, 10) : 0;

                // Validate accepted version
                if (acceptedVersion < 0) {
                    console.error('[Terms Check] Invalid accepted version:', acceptedVersion);
                    setLoading(false);
                    setVisible(false);
                    return;
                }

                // Show modal if:
                // 1. User hasn't accepted any version for this building
                // 2. Terms version is higher than accepted version AND requireReacceptance is true
                const shouldShowTerms = !acceptedVersion || (terms.version > acceptedVersion && terms.requireReacceptance);

                setVisible(shouldShowTerms);
            } catch (error) {
                console.error('[Terms Check] Error checking terms:', error);
                message.error('Error checking terms of use status');
                setVisible(false);
            } finally {
                setLoading(false);
            }
        };

        // Only check terms when we have both user and building context
        if (buildingId && user) {
            checkTermsAcceptance();
        }
    }, [buildingId, user, buildingSettings]);

    const handleAccept = async () => {
        if (!buildingId || !user) {
            message.error('Missing required context for accepting terms');
            return;
        }

        setSubmitting(true);
        try {
            // Get the latest user state in case it was updated by other modals
            const { store } = require('../../redux');
            const currentUser = store.getState().app.user;
            // Get current terms version
            const termsResponse = await API.graphql(graphqlOperation(getTermsQuery, {
                pk: buildingId
            }));

            const terms = termsResponse?.data?.getTermsOfUse;
            if (!terms) {
                throw new Error('Could not get current terms version');
            }

            // Add validation for terms version
            if (terms.version < 1) {
                console.error('[Terms Error] Invalid terms version:', terms.version);
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Get current accepted version
            const currentAcceptedVersionField = currentUser.extraFields?.find(field => field.key === `acceptedTermsVersion_${buildingId}`);
            const currentAcceptedVersion = currentAcceptedVersionField ? parseInt(currentAcceptedVersionField.value, 10) : 0;

            // Validate that new version is greater than current accepted version
            if (terms.version <= currentAcceptedVersion) {
                console.error('[Terms Error] New version not greater than accepted version:', {
                    currentAcceptedVersion,
                    newVersion: terms.version
                });
                message.error('Invalid terms version. Please contact support.');
                return;
            }

            // Update extraFields with accepted version for this specific building
            const extraFields = [...(currentUser.extraFields || [])];
            const versionIndex = extraFields.findIndex(field => field.key === `acceptedTermsVersion_${buildingId}`);

            if (versionIndex >= 0) {
                extraFields[versionIndex].value = terms.version.toString();
            } else {
                extraFields.push({
                    key: `acceptedTermsVersion_${buildingId}`,
                    value: terms.version.toString()
                });
            }

            // Try the update with retry logic for version conflicts
            let response;
            let retryCount = 0;
            const maxRetries = 3;

            while (retryCount < maxRetries) {
                try {
                    response = await API.graphql(graphqlOperation(updateOccupantQuery, {
                        pk: currentUser.pk,
                        sk: currentUser.sk,
                        expectedVersion: currentUser.version,
                        input: {
                            extraFields
                        }
                    }));
                    break; // Success, exit retry loop
                } catch (error) {
                    if (error.errors?.[0]?.errorType === 'DynamoDB:ConditionalCheckFailedException' && retryCount < maxRetries - 1) {
                        // Version conflict, wait and retry - get fresh user state
                        retryCount++;

                        // Force a small delay to allow other operations to complete
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        // Get fresh user object from Redux state
                        const { store: freshStore } = require('../../redux');
                        const freshUser = freshStore.getState().app.user;
                        if (freshUser && freshUser.version > currentUser.version) {
                            // Update the currentUser reference for the next iteration
                            Object.assign(currentUser, freshUser);
                        }
                        continue;
                    }
                    throw error; // Re-throw if not a version conflict or max retries reached
                }
            }

            if (response?.data?.updateOccupant) {
                // Merge onto the freshest store user (it has d1, which the
                // UPDATE_USER_DATA reducer requires) so the terms check sees
                // the new accepted version and never re-arms the popup.
                const { store: latestStore } = require('../../redux');
                const latestUser = latestStore.getState().app.user;
                updateUser({
                    ...latestUser,
                    extraFields: response.data.updateOccupant.extraFields || extraFields,
                    version: response.data.updateOccupant.version
                });
                // Dispatch event to notify other components that occupant data was updated
                window.dispatchEvent(new CustomEvent('occupant-data-updated'))
                setVisible(false);
                message.success('Terms of Use accepted');
            }
        } catch (error) {
            console.error('[Terms Error] Error accepting terms:', error);
            message.error('Failed to accept Terms of Use. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!effectiveVisible || !buildingId || !user) {
        return null;
    }

    return (
        <Overlay
            visible={true}
            closebutton={false}
            minimal={true}
            zIndex={2020}
        >
            <div style={styles.container}>
                <div style={{ textAlign: 'center' }}>
                    <div style={styles.infoContainer}>
                        <b>{building?.name}</b>
                        <span style={{ color: '#606060' }}>{tenant?.name}</span>
                    </div>
                    <div style={styles.title}>
                        TERMS OF USE
                    </div>
                </div>
                <div style={styles.content}>
                    {termsText}
                </div>
                <div style={styles.buttonContainer}>
                    <Button
                        type="primary"
                        onClick={handleAccept}
                        loading={submitting}
                    >
                        Accept
                    </Button>
                </div>
            </div>
        </Overlay>
    );
};

export default TermsOfUseAcceptance;
